/**
 * T051: WebSocket Service Implementation
 * Task 13: Real-Time Broadcasting with Laravel Echo
 * 
 * Real-time communication service for project messaging and broadcasting
 * Constitutional compliance: Performance <2s, error handling, reconnection logic
 */

import type { Socket } from 'socket.io-client';
// Dynamically imported in initializeEcho() to handle missing dependencies gracefully
// import Pusher from 'pusher-js';
// import Echo from 'laravel-echo';

export interface Message {
  id: number;
  project_id: number;
  sender_id: number;
  content: string;
  attachment_path?: string;
  attachment_name?: string;
  attachment_mime_type?: string;
  attachment_size?: number;
  read_at?: string;
  read_by?: number;
  reply_to_id?: number;
  is_system_message: boolean;
  metadata?: any;
  created_at: string;
  updated_at: string;
  sender: {
    id: number;
    name: string;
    email: string;
    role: string;
  };
  replyTo?: Message;
  attachment_url?: string;
  is_read: boolean;
  time_ago: string;
}

export interface MessageSentEvent {
  message: Message;
  timestamp: string;
}

// Laravel Broadcasting Event Interfaces
export interface ProjectAssignedEvent {
  assignment: {
    id: number;
    project_id: number;
    developer_id: number;
    role: string;
    status: string;
    hourly_rate: number;
    allocated_hours: number;
    start_date: string;
    end_date: string;
  };
  project: {
    id: number;
    name: string;
    description: string;
    status: string;
    client_id: number;
  };
  developer: {
    id: number;
    name: string;
    email: string;
  };
  assignedBy: {
    id: number;
    name: string;
  };
}

export interface TaskAssignedEvent {
  task: {
    id: number;
    title: string;
    description: string;
    status: string;
    priority: string;
    due_date?: string;
    project_id: number;
  };
  assignee: {
    id: number;
    name: string;
    email: string;
  };
  assigner: {
    id: number;
    name: string;
  };
}

export interface StatusUpdatePostedEvent {
  update: {
    id: number;
    content: string;
    status: 'on_track' | 'at_risk' | 'blocked' | 'completed';
    progress: number;
    achievements?: string[];
    next_steps?: string[];
    blockers?: string[];
    hours_worked?: number;
  };
  author: {
    id: number;
    name: string;
  };
  project: {
    id: number;
    name: string;
  };
}

export interface ProjectStatusChangedEvent {
  project: {
    id: number;
    name: string;
    description: string;
  };
  oldStatus: string;
  newStatus: string;
  changedBy: {
    id: number;
    name: string;
  };
}

type EventCallback = (data: any) => void;

class WebSocketService {
  private socket: Socket | null = null;
  private echo: any | null = null; // Laravel Echo instance (using any to avoid type issues before install)
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 5;
  private reconnectDelay: number = 1000; // Start with 1 second
  private eventCallbacks: Map<string, EventCallback[]> = new Map();
  private token: string | null = null;
  private subscribedChannels: Set<string> = new Set();

  /**
   * Initialize WebSocket connection (Socket.IO for messaging)
   */
  async connect(token: string): Promise<void> {
    return new Promise(async (resolve, reject) => {
      try {
        this.token = token;
        
        // For development, we'll simulate WebSocket functionality
        // In production, this would connect to a real WebSocket server
        if (process.env.NODE_ENV === 'development') {
          console.log('WebSocket Service: Simulating connection in development');
          this.isConnected = true;
          this.simulateConnection();
          resolve();
          return;
        }

        // Real WebSocket connection (for production)
        const {io} = await import('socket.io-client');
        this.socket = io(process.env.REACT_APP_WS_URL || 'ws://localhost:6001', {
          auth: {
            token: token
          },
          transports: ['websocket'],
          timeout: 5000,
        });

        this.socket.on('connect', () => {
          console.log('WebSocket connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          resolve();
        });

        this.socket.on('disconnect', () => {
          console.log('WebSocket disconnected');
          this.isConnected = false;
          this.handleReconnect();
        });

        this.socket.on('connect_error', (error: Error) => {
          console.error('WebSocket connection error:', error);
          this.isConnected = false;
          if (this.reconnectAttempts === 0) {
            reject(error);
          }
          this.handleReconnect();
        });

        // Listen for message events
        this.socket.on('message.sent', (data: MessageSentEvent) => {
          this.triggerEvent('message.sent', data);
        });

      } catch (error) {
        console.error('Error initializing WebSocket:', error);
        reject(error);
      }
    });
  }

  /**
   * Initialize Laravel Echo for broadcasting
   */
  async initializeEcho(token: string): Promise<void> {
    try {
      this.token = token;

      // Import packages dynamically to handle missing dependencies gracefully
      const [PusherModule, EchoModule] = await Promise.all([
        import('pusher-js'),
        import('laravel-echo')
      ]);

      const PusherConstructor = PusherModule.default;
      const EchoConstructor = EchoModule.default;

      // Configure Laravel Echo with Pusher
      this.echo = new EchoConstructor({
        broadcaster: 'pusher',
        key: process.env.REACT_APP_PUSHER_APP_KEY,
        cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER || 'mt1',
        forceTLS: true,
        authEndpoint: `${process.env.REACT_APP_API_URL}/broadcasting/auth`,
        auth: {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        },
        // Pass Pusher constructor
        client: new PusherConstructor(process.env.REACT_APP_PUSHER_APP_KEY!, {
          cluster: process.env.REACT_APP_PUSHER_APP_CLUSTER || 'mt1',
          forceTLS: true,
          authEndpoint: `${process.env.REACT_APP_API_URL}/broadcasting/auth`,
          auth: {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json'
            }
          }
        })
      });

      console.log('Laravel Echo initialized successfully');
    } catch (error) {
      console.error('Failed to initialize Laravel Echo:', error);
      // Gracefully degrade if packages not installed
      console.warn('Running without real-time broadcasting. Install pusher-js and laravel-echo for full functionality.');
    }
  }

  /**
   * Simulate connection for development
   */
  private simulateConnection(): void {
    // Simulate successful connection
    setTimeout(() => {
      console.log('WebSocket Service: Simulated connection established');
    }, 100);
  }

  /**
   * Handle reconnection attempts
   */
  private handleReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('Max reconnection attempts reached');
      return;
    }

    this.reconnectAttempts++;
    console.log(`Attempting to reconnect... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (this.token) {
        this.connect(this.token).catch(console.error);
      }
    }, this.reconnectDelay);

    // Exponential backoff
    this.reconnectDelay = Math.min(this.reconnectDelay * 2, 30000);
  }

  /**
   * Join a project room for real-time updates
   */
  joinProject(projectId: number): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`WebSocket Service: Simulating join project ${projectId}`);
      return;
    }

    if (this.socket && this.isConnected) {
      this.socket.emit('join', `project.${projectId}`);
    }
  }

  /**
   * Leave a project room
   */
  leaveProject(projectId: number): void {
    if (process.env.NODE_ENV === 'development') {
      console.log(`WebSocket Service: Simulating leave project ${projectId}`);
      return;
    }

    if (this.socket && this.isConnected) {
      this.socket.emit('leave', `project.${projectId}`);
    }
  }

  /**
   * Subscribe to events
   */
  on(event: string, callback: EventCallback): void {
    if (!this.eventCallbacks.has(event)) {
      this.eventCallbacks.set(event, []);
    }
    this.eventCallbacks.get(event)!.push(callback);
  }

  /**
   * Unsubscribe from events
   */
  off(event: string, callback: EventCallback): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Trigger event callbacks
   */
  private triggerEvent(event: string, data: any): void {
    const callbacks = this.eventCallbacks.get(event);
    if (callbacks) {
      callbacks.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event callback for ${event}:`, error);
        }
      });
    }
  }

  /**
   * Simulate receiving a message (for development/testing)
   */
  simulateMessage(message: Message): void {
    if (process.env.NODE_ENV === 'development') {
      this.triggerEvent('message.sent', {
        message,
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Subscribe to user-specific private channel
   */
  subscribeToUser(userId: number): void {
    if (!this.echo) {
      console.warn('Laravel Echo not initialized. Call initializeEcho() first.');
      return;
    }

    const channelName = `user.${userId}`;
    if (this.subscribedChannels.has(channelName)) {
      return; // Already subscribed
    }

    const channel = this.echo.private(channelName);

    // Listen for ProjectAssigned event
    channel.listen('.project.assigned', (event: ProjectAssignedEvent) => {
      console.log('Project assigned:', event);
      this.triggerEvent('project.assigned', event);
    });

    // Listen for TaskAssigned event
    channel.listen('.task.assigned', (event: TaskAssignedEvent) => {
      console.log('Task assigned:', event);
      this.triggerEvent('task.assigned', event);
    });

    this.subscribedChannels.add(channelName);
    console.log(`Subscribed to user channel: ${channelName}`);
  }

  /**
   * Subscribe to project-specific private channel
   */
  subscribeToProject(projectId: number): void {
    if (!this.echo) {
      console.warn('Laravel Echo not initialized. Call initializeEcho() first.');
      return;
    }

    const channelName = `project.${projectId}`;
    if (this.subscribedChannels.has(channelName)) {
      return; // Already subscribed
    }

    const channel = this.echo.private(channelName);

    // Listen for StatusUpdatePosted event
    channel.listen('.status.update.posted', (event: StatusUpdatePostedEvent) => {
      console.log('Status update posted:', event);
      this.triggerEvent('status.update.posted', event);
    });

    // Listen for ProjectStatusChanged event
    channel.listen('.project.status.changed', (event: ProjectStatusChangedEvent) => {
      console.log('Project status changed:', event);
      this.triggerEvent('project.status.changed', event);
    });

    // Also listen for ProjectAssigned (admins assigning developers)
    channel.listen('.project.assigned', (event: ProjectAssignedEvent) => {
      console.log('Developer assigned to project:', event);
      this.triggerEvent('project.assigned', event);
    });

    // Also listen for TaskAssigned (within project context)
    channel.listen('.task.assigned', (event: TaskAssignedEvent) => {
      console.log('Task assigned in project:', event);
      this.triggerEvent('task.assigned', event);
    });

    this.subscribedChannels.add(channelName);
    console.log(`Subscribed to project channel: ${channelName}`);
  }

  /**
   * Unsubscribe from user channel
   */
  unsubscribeFromUser(userId: number): void {
    if (!this.echo) return;

    const channelName = `user.${userId}`;
    if (this.subscribedChannels.has(channelName)) {
      this.echo.leave(channelName);
      this.subscribedChannels.delete(channelName);
      console.log(`Unsubscribed from user channel: ${channelName}`);
    }
  }

  /**
   * Unsubscribe from project channel
   */
  unsubscribeFromProject(projectId: number): void {
    if (!this.echo) return;

    const channelName = `project.${projectId}`;
    if (this.subscribedChannels.has(channelName)) {
      this.echo.leave(channelName);
      this.subscribedChannels.delete(channelName);
      console.log(`Unsubscribed from project channel: ${channelName}`);
    }
  }

  /**
   * Disconnect WebSocket and Echo
   */
  disconnect(): void {
    // Disconnect Socket.IO
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }

    // Disconnect Laravel Echo
    if (this.echo) {
      this.subscribedChannels.forEach(channelName => {
        this.echo.leave(channelName);
      });
      this.echo.disconnect();
      this.echo = null;
    }

    this.subscribedChannels.clear();
    this.isConnected = false;
    this.eventCallbacks.clear();
  }

  /**
   * Check connection status
   */
  isSocketConnected(): boolean {
    return this.isConnected;
  }

  /**
   * Check Echo connection status
   */
  isEchoConnected(): boolean {
    return this.echo !== null;
  }

  /**
   * Get connection status for diagnostics
   */
  getConnectionStatus(): {
    connected: boolean;
    echoConnected: boolean;
    reconnectAttempts: number;
    maxReconnectAttempts: number;
    subscribedChannels: string[];
  } {
    return {
      connected: this.isConnected,
      echoConnected: this.echo !== null,
      reconnectAttempts: this.reconnectAttempts,
      maxReconnectAttempts: this.maxReconnectAttempts,
      subscribedChannels: Array.from(this.subscribedChannels),
    };
  }
}

// Export singleton instance
const websocketService = new WebSocketService();
export default websocketService;